import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { Button } from './ui';
import { IconClose, IconStar } from './icons';

const LABELS = ['Poor', 'Could be better', 'Fine', 'Good', 'Excellent'];

/**
 * A small pill in the bottom right: "Rate your experience". Opens a card
 * with five stars and an optional line of comment, sends, says thanks, and
 * folds away. Quiet on purpose: it sits under every screen, so anything
 * louder would compete with the work.
 */
export function FeedbackWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [thanked, setThanked] = useState(false);
  const panel = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: () => api.sendFeedback({ rating, comment: comment.trim() || undefined, path: pathname }),
    onSuccess: () => {
      setThanked(true);
      setRating(0);
      setComment('');
      window.setTimeout(() => {
        setThanked(false);
        setOpen(false);
      }, 2500);
    },
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const shown = hover || rating;

  return (
    <div className="fixed right-4 bottom-4 z-30 flex flex-col items-end gap-2 print:hidden">
      {open && (
        <div
          ref={panel}
          role="dialog"
          aria-label="Rate your experience"
          className="animate-rise w-72 rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Rate your experience</h2>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="-mt-1 -mr-1 rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              <IconClose className="h-4 w-4" />
            </button>
          </div>

          {thanked ? (
            <p role="status" className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">
              Thank you. That goes straight to the people who look after this system.
            </p>
          ) : (
            <>
              <div className="mt-3 flex items-center gap-1" role="radiogroup" aria-label="Stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    role="radio"
                    aria-checked={rating === star}
                    aria-label={`${star} star${star === 1 ? '' : 's'}: ${LABELS[star - 1]}`}
                    onMouseEnter={() => setHover(star)}
                    onMouseLeave={() => setHover(0)}
                    onFocus={() => setHover(star)}
                    onBlur={() => setHover(0)}
                    onClick={() => setRating(star)}
                    className={`press rounded-md p-1 transition-colors ${
                      star <= shown ? 'text-copper-500' : 'text-slate-300 dark:text-slate-600'
                    }`}
                  >
                    <IconStar className="h-6 w-6" filled={star <= shown} />
                  </button>
                ))}
                <span className="ml-1 text-xs text-slate-500 dark:text-slate-400" aria-live="polite">
                  {shown ? LABELS[shown - 1] : ''}
                </span>
              </div>

              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                rows={2}
                maxLength={1000}
                placeholder="Anything you want to add (optional)"
                aria-label="Comment"
                className="mt-3 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-400"
              />

              {send.isError && (
                <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
                  {send.error instanceof ApiError ? send.error.message : 'Could not send that. Try again in a moment.'}
                </p>
              )}

              <div className="mt-3 flex justify-end">
                <Button size="sm" disabled={rating === 0 || send.isPending} onClick={() => send.mutate()}>
                  {send.isPending ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label="Rate your experience"
        className="press flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-3 py-2 text-xs font-medium text-slate-700 shadow-md backdrop-blur-sm transition-colors hover:border-slate-300 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-white"
      >
        <IconStar className="h-4 w-4 text-copper-500" />
        <span className="hidden sm:inline">Rate your experience</span>
      </button>
    </div>
  );
}
